// Pure helpers for the extension post-signup trial toast/chip and welcome modal (#693).
// Kept free of React so they unit-test without an RTL harness (this repo has none).

import type { NudgeEventProps } from '@bt-common/nudges/nudge.types';

/** Wire shape of the `pro-trial-status` action. `daysLeft` is computed server-side. */
export interface ProTrialStatus {
  active: boolean;
  trialEnd?: string;
  daysLeft?: number;
}

export const EXT_WELCOME_NUDGE_ID = 'ext_welcome_2026q4' as const;

/** Days left to show, or null when there is no live trial (chip hidden). */
export function trialDaysLeft(status: ProTrialStatus | null | undefined): number | null {
  if (!status?.active) return null;
  const d = status.daysLeft;
  return typeof d === 'number' && Number.isFinite(d) && d >= 1 ? Math.floor(d) : null;
}

/** The post-signup welcome flow runs once, for a fresh extension signup that is on a live trial. */
export function shouldRunWelcome(f: { justSignedUp: boolean; trialActive: boolean }): boolean {
  return f.justSignedUp && f.trialActive;
}

/** The Gateway step is pointless for a user who already has one connected. */
export function shouldShowWelcomeModal(f: { welcomeRunning: boolean; isGatewayConnected: boolean }): boolean {
  return f.welcomeRunning && !f.isGatewayConnected;
}

/** Event props for the welcome modal's nudge_* events (extension only, no holdout arm). */
export function welcomeEventProps(): NudgeEventProps {
  return { nudgeId: EXT_WELCOME_NUDGE_ID, arm: 'send', surface: 'extension' };
}

/** App paths opened in a new tab from the extension. */
export const PLANS_PATH = '/account-subscription';
export const GATEWAY_PATH = '/connections?via=ext_welcome';

/** chrome.storage.local key carrying "this user just signed up in the extension" across contexts. */
export const SIGNUP_FLAG_KEY = 'btExtJustSignedUp';
/** A flag older than this is stale (the signup surface was e.g. the toolbar popup with no WhatsApp tab open). */
export const SIGNUP_FLAG_MAX_AGE_MS = 10 * 60 * 1000;

/** True when `value` is a fresh signup timestamp (ms). */
export function isFreshSignupFlag(value: unknown, now: number): boolean {
  return typeof value === 'number' && Number.isFinite(value) && now - value >= 0 && now - value <= SIGNUP_FLAG_MAX_AGE_MS;
}
